import { forwardRef, ReactNode, Ref, useMemo, useRef } from 'react';

import { Badge, Stack, useTheme } from '@mui/material';
import {
  useGridApiContext,
  GridToolbarProps,
  GridPreferencePanelsValue,
  GridToolbarQuickFilter,
  GridFilterModel,
} from '@mui/x-data-grid-pro';

import { IcFilter, IcSettings } from '@/assets/images';
import { NarrowButton } from '@/components';
import { toolbarStyles } from '@/components/DataGrid/styles';

export interface DataGridToolbarProps extends GridToolbarProps {
  cta?: ReactNode;
  hideFilter?: boolean;
  hideQuickFilter?: boolean;
  filter?: GridFilterModel;
  searchPlaceholder?: string;
}

export const DataGridToolbar = forwardRef<HTMLDivElement, DataGridToolbarProps>(
  function CustomDataGridToolbar(
    { cta, hideFilter, hideQuickFilter, filter, searchPlaceholder, ...rest },
    ref,
  ) {
    const apiRef = useGridApiContext();
    const theme = useTheme();

    // GridPanel's ClickAwayListener uses mouseEvent="onPointerUp", which fires
    // before onClick. By the time onClick runs the panel state is already closed,
    // so we snapshot whether the panel was open on pointerdown and skip reopening it.
    const panelOpenOnPointerDown = useRef<GridPreferencePanelsValue | null>(
      null,
    );

    const handlePanelPointerDown = (panel: GridPreferencePanelsValue) => {
      if (!apiRef?.current) return;
      const { open, openedPanelValue } = apiRef.current.state.preferencePanel;
      panelOpenOnPointerDown.current =
        open && openedPanelValue === panel ? panel : null;
    };

    const handlePanelClick = (panel: GridPreferencePanelsValue) => {
      if (!apiRef?.current) return;
      const wasOpen = panelOpenOnPointerDown.current === panel;
      panelOpenOnPointerDown.current = null;
      if (!wasOpen) {
        apiRef.current.showPreferences(panel);
      }
    };

    const handleColumnsPanel = () =>
      handlePanelClick(GridPreferencePanelsValue.columns);
    const handleFiltersPanel = () =>
      handlePanelClick(GridPreferencePanelsValue.filters);

    const activeFilterCount = useMemo(() => {
      return filter?.items?.length || 0;
    }, [filter]);

    const isFiltering = activeFilterCount > 0;

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        padding="8px 12px"
        minHeight="54px"
        boxSizing="border-box"
        sx={toolbarStyles(theme).GridFilterPanel}
        ref={ref as Ref<HTMLDivElement>}
        {...rest}
      >
        <Stack spacing="4px" direction="row">
          <Stack position="relative">
            {!hideFilter ? (
              <Badge
                overlap="circular"
                color="primary"
                badgeContent=""
                invisible={!isFiltering}
                sx={{
                  '& .MuiBadge-badge': {
                    minWidth: '14px',
                    height: '14px',
                    padding: 0,
                  },
                }}
              >
                <NarrowButton
                  variant={isFiltering ? 'contained' : 'contained'}
                  color="inherit"
                  onPointerDown={() =>
                    handlePanelPointerDown(GridPreferencePanelsValue.filters)
                  }
                  onClick={handleFiltersPanel}
                  sx={{ width: '36px', height: '36px' }}
                >
                  <IcFilter />
                </NarrowButton>
              </Badge>
            ) : null}
          </Stack>
          <NarrowButton
            variant="contained"
            color="inherit"
            onPointerDown={() =>
              handlePanelPointerDown(GridPreferencePanelsValue.columns)
            }
            onClick={handleColumnsPanel}
            sx={{ width: '36px', height: '36px' }}
          >
            <IcSettings color={theme.palette.text.primary} />
          </NarrowButton>
        </Stack>
        <Stack
          spacing="12px"
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{ flex: 1, minWidth: 0 }}
        >
          {!hideQuickFilter ? (
            <GridToolbarQuickFilter
              sx={toolbarStyles(theme).GridToolbarQuickFilter}
              variant="outlined"
              debounceMs={800}
              placeholder={searchPlaceholder ? searchPlaceholder : 'Search...'}
            />
          ) : null}
          {cta}
        </Stack>
      </Stack>
    );
  },
);
